import { Form, Input, Radio, RadioChangeEvent, Space } from 'antd'
import { FormInstance } from 'antd/lib/form/Form'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MultiColumnFormColumnContainer,
  MultiColumnFormContentContainer,
  SelectOfrDocumentTemplate,
  SelectParticipant,
  SelectUser,
} from '../../../components'
import { OfrDocumentSelectOption } from '../../../components/SelectOfrDocumentTemplate'
import {
  ofrTemplateTypes,
  privacyTypes,
  workspaceTypes,
} from '../../../constants/constants'
import useWorkspaceDetails from '../../../hooks/useWorkspaceDetails'
import { User } from '../../../store/reducers/userReducer'
import { WorkspaceLead } from '../../../store/reducers/workspaceReducer'

interface WorkspaceSettingsInfoPanelProps {
  form: FormInstance
}
const WorkspaceSettingsInfoPanel = ({
  form,
}: WorkspaceSettingsInfoPanelProps) => {
  const { selectedWorkspace, getPermittedUsersOfWorkspace, isUpdatable } =
    useWorkspaceDetails({
      preventFetch: true,
    })

  const [isPrivate, setIsPrivate] = useState(
    selectedWorkspace?.visibility === privacyTypes.PRIVATE
  )
  const [isSelectDisabled, setIsSelectDisabled] = useState(
    selectedWorkspace?.visibility === privacyTypes.ORGANIZATION || false
  )

  const [initialParticipants, setInitialParticipants] = useState<string[]>(
    selectedWorkspace?.participants?.map((participant) => participant.id) || []
  )
  const [selectLoading, setSelectLoading] = useState(false)
  const [permittedUsers, setPermittedUsers] = useState<User[]>([])

  const isQualityReviewWorkspace = useMemo(() => {
    return selectedWorkspace?.typeCode === workspaceTypes.OFR_QR
  }, [selectedWorkspace])

  const handleRadioChange = useCallback((e: RadioChangeEvent) => {
    e.preventDefault()
    setIsPrivate(e.target.value === privacyTypes.PRIVATE)
    setIsSelectDisabled(e.target.value === privacyTypes.ORGANIZATION)
  }, [])

  useEffect(() => {
    const setParticipantsAndValues = () => {
      if (!selectedWorkspace) return

      const currentVisibility = form.getFieldValue('visibility')
      const isPrivate = currentVisibility === privacyTypes.PRIVATE

      setIsPrivate(isPrivate)
      setIsSelectDisabled(!isPrivate)

      const participants = isPrivate
        ? selectedWorkspace.participants?.map(
            (participant) => participant.id
          ) || []
        : []

      setInitialParticipants(participants)
      form.setFieldsValue({
        participants: participants,
        config: {
          ofrDocumentTemplatesId:
            selectedWorkspace?.config?.ofrDocumentTemplatesId || '',
          ofrDocumentTemplates:
            selectedWorkspace?.config?.ofrDocumentTemplates || '',
          ofrDocumentTemplatesName:
            selectedWorkspace?.config?.ofrDocumentTemplatesName || '',
        },
      })
    }

    setParticipantsAndValues()
  }, [selectedWorkspace, form])

  const handleChangeOfrDocumentTemplates = useCallback(
    (value: OfrDocumentSelectOption | undefined) => {
      if (value) {
        form.setFieldsValue({
          config: {
            ofrDocumentTemplatesId: value.value,
            ofrDocumentTemplates: value.documentId,
            ofrDocumentTemplatesName: value.label,
          },
        })
      } else {
        form.setFieldsValue({
          config: {
            ofrDocumentTemplatesId: '',
            ofrDocumentTemplates: '',
            ofrDocumentTemplatesName: '',
          },
        })
      }
    },
    [form]
  )

  const handleChangeWorkspaceLead = useCallback(
    (value: WorkspaceLead | undefined) => {
      if (value) {
        const currentConfig = form.getFieldValue('config')
        form.setFieldsValue({
          config: {
            ...currentConfig,
            workspaceLead: value,
          },
        })
      }
    },
    [form]
  )

  useEffect(() => {
    const fetchPermittedUsers = async () => {
      setSelectLoading(true)
      try {
        const users = await getPermittedUsersOfWorkspace()
        setPermittedUsers(users)
      } catch (error) {
        console.error('Error fetching permitted users:', error)
      } finally {
        setSelectLoading(false)
      }
    }
    fetchPermittedUsers()
  }, [getPermittedUsersOfWorkspace, selectedWorkspace])

  return (
    <MultiColumnFormContentContainer>
      <MultiColumnFormColumnContainer>
        <Form.Item
          label={t('Title')}
          required
          name="title"
          rules={[
            { required: true, message: t('errorMessage.isRequired') || '' },
          ]}
        >
          <Input width={'100%'} placeholder={'Title'} />
        </Form.Item>
        <Form.Item label={t('Description')} name="description">
          <Input width={'100%'} placeholder={'Description'} />
        </Form.Item>
        {selectedWorkspace?.visibility && (
          <Form.Item
            label={t('Privacy Settings')}
            name="visibility"
            rules={[
              {
                required: true,
                message: t('errorMessage.isRequired') || '',
              },
            ]}
          >
            <Radio.Group
              buttonStyle="solid"
              defaultValue={selectedWorkspace?.visibility}
              onChange={handleRadioChange}
            >
              <Space direction="vertical">
                <Radio value={privacyTypes.PRIVATE}>
                  {t('createWorkspaceModal.privacyStep.privateOption')}
                </Radio>
                <Radio
                  value={privacyTypes.ORGANIZATION}
                  disabled={
                    selectedWorkspace?.dataSource.visibility ===
                    privacyTypes.PRIVATE
                  }
                >
                  {t('createWorkspaceModal.privacyStep.organizationalOption')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item
          label={t('createWorkspaceModal.privacyStep.participantSelectHeader')}
          name="participants"
          rules={[
            {
              required: isPrivate,
              message: t('errorMessage.isRequired') || '',
            },
          ]}
          extra={
            t('createWorkspaceModal.privacyStep.extraInputPlaceholder') || ''
          }
        >
          <SelectParticipant
            loading={selectLoading}
            disabled={isSelectDisabled || !isUpdatable}
            initialParticipants={initialParticipants}
            allParticipants={permittedUsers}
          />
        </Form.Item>
        {isQualityReviewWorkspace && (
          <>
            <Form.Item
              label={t(
                'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.qualityAnnotation'
              )}
              name={['config', 'ofrDocumentTemplates']}
              rules={[
                {
                  required: isPrivate,
                  message: t('errorMessage.isRequired') || '',
                },
              ]}
              extra={
                t(
                  'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.qualityAnnotationSelectionDescription'
                ) || ''
              }
            >
              <SelectOfrDocumentTemplate
                disabled={!isQualityReviewWorkspace || !isUpdatable}
                dataSourceId={selectedWorkspace?.dataSource.id}
                initalDocument={{
                  key: selectedWorkspace?.config?.ofrDocumentTemplatesId || '',
                  value:
                    selectedWorkspace?.config?.ofrDocumentTemplatesId || '',
                  label:
                    selectedWorkspace?.config?.ofrDocumentTemplatesName || '',
                  documentId:
                    selectedWorkspace?.config?.ofrDocumentTemplates || '',
                }}
                onChange={handleChangeOfrDocumentTemplates}
                type={ofrTemplateTypes.QUALITY}
              />
            </Form.Item>
            <Form.Item
              name={['config', 'ofrDocumentTemplatesName']} // Separate field for the file name
              noStyle
            >
              <Input type="hidden" />{' '}
              {/* Hidden field to hold the name value */}
            </Form.Item>
            <Form.Item
              name={['config', 'ofrDocumentTemplatesId']} // Separate field for the file id
              noStyle
            >
              <Input type="hidden" />{' '}
              {/* Hidden field to hold the name value */}
            </Form.Item>

            <Form.Item
              label={t(
                'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.protocolAnnotation'
              )}
              name={['config', 'ofrDocumentTemplates']}
              rules={[
                {
                  required: isPrivate,
                  message: t('errorMessage.isRequired') || '',
                },
              ]}
              extra={
                t(
                  'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.protocolAnnotationSelectionDescription'
                ) || ''
              }
            >
              <SelectOfrDocumentTemplate
                disabled={!isQualityReviewWorkspace || !isUpdatable}
                dataSourceId={selectedWorkspace?.dataSource.id}
                type={ofrTemplateTypes.PROTOCOL}
              />
            </Form.Item>
            <Form.Item
              name={['config', 'ofrDocumentTemplatesName']} // Separate field for the file name
              noStyle
            >
              <Input type="hidden" />{' '}
              {/* Hidden field to hold the name value */}
            </Form.Item>
            <Form.Item
              name={['config', 'ofrDocumentTemplatesId']} // Separate field for the file id
              noStyle
            >
              <Input type="hidden" />{' '}
              {/* Hidden field to hold the name value */}
            </Form.Item>
          </>
        )}
      </MultiColumnFormColumnContainer>
      <MultiColumnFormColumnContainer>
        {isQualityReviewWorkspace && (
          <Form.Item
            label={t(
              'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.workspaceLead'
            )}
            name={['config', 'workspaceLead']}
            rules={[
              {
                required: true,
                message: t('errorMessage.isRequired') || '',
              },
            ]}
            initialValue={selectedWorkspace?.config?.workspaceLead}
            extra={t(
              'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.workspaceLeadDescription'
            )}
          >
            <SelectUser
              disabled={!isUpdatable}
              initialValue={selectedWorkspace?.config?.workspaceLead?.id}
              allUsers={permittedUsers}
              onChange={handleChangeWorkspaceLead}
              placeholder={t(
                'workspaceDetailsPage.workspaceSettingsPanel.conformityCheckSettings.placeholder'
              )}
            />
          </Form.Item>
        )}
      </MultiColumnFormColumnContainer>
    </MultiColumnFormContentContainer>
  )
}

export default WorkspaceSettingsInfoPanel
